/* eslint-disable no-unused-vars */
import React, { useState } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/styles";
import {
  Typography,
  Avatar,
  RadioGroup,
  FormControlLabel,
  Radio,
  Grid,
  Divider,
} from "@material-ui/core";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Toolbar,
  Box,
  FormControl,
  Tabs,
  Tab,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
} from "@mui/material";
import { ArrowForwardIosSharp } from "@mui/icons-material";
import { v4 as uuidv4 } from "uuid";
import { InfoTwoTone } from "@material-ui/icons";
import { FilesUploader } from "src/components";
import PopupImg from "src/components/PopupImg/PopupImg.Component";
import { useTranslation } from "react-i18next";

const useStyles = makeStyles((theme) => ({
  root: {
    height: "100%",
  },
  accordion: {
    "&:before": {
      display: "none",
    },
  },
  accordionTitle: {
    marginLeft: theme.spacing(2),
    marginBottom: 10,
  },
  accordionSummary: {
    flexDirection: "row-reverse",
    "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
      transform: "rotate(90deg)",
    },
    "& .MuiAccordionSummary-content": {
      marginLeft: theme.spacing(2) + "px !important",
      marginTop: theme.spacing(2) + "px !important",
      marginBottom: theme.spacing(2) + "px !important",
    },
    backgroundColor: "#eaeaea !important",
    minHeight: "64px !important",
  },
  subAccordionSummary: {
    flexDirection: "row-reverse",
    "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
      transform: "rotate(90deg)",
    },
    "& .MuiAccordionSummary-content": {
      marginLeft: theme.spacing(2) + "px !important",
      marginTop: theme.spacing(2) + "px !important",
      marginBottom: theme.spacing(2) + "px !important",
    },
    backgroundColor: "#eaeaea !important",
    minHeight: "64px !important",
  },
  accordionDetails: {
    padding: theme.spacing(2),
  },
  flexGrow: {
    flexGrow: 1,
  },
  toolbar: {
    minHeight: "35px !important",
    textAlign: "center",
    fontWeight: "bold",
  },
  subSectionHeader: {
    backgroundColor: "#dff6ff",
    padding: theme.spacing(1, 2),
    fontWeight: "bold",
  },
  docimg: {
    width: "70%",
  },
  docLayout: {
    textAlign: "center",
    borderLeft: 1,
    borderColor: "divider",
  },
  iconInfo: {
    color: "#ff9300",
    "&:hover": {
      cursor: "pointer",
    },
  },
  isValid: {
    color: "#058C46 !important",
    backgroundColor: "#CDFFE4 !important",
  },
  inProgress: {
    color: "#A7A004 !important",
    backgroundColor: "#FFFDC8 !important",
    fontWeight: "bold !important",
  },
}));

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && (
        <Box sx={{ p: 3 }}>
          <Typography component={"span"}>{children}</Typography>
        </Box>
      )}
    </div>
  );
}

function QuestionSet({ data, isViewMode, onComplete, className, ...rest }) {
  const classes = useStyles();
  const [tabValue, setTabValue] = useState(0);
  const [sectionExpanded, setSectionExpanded] = useState(0);
  const [subSectionExpanded, setSubSectionExpanded] = useState(0);
  const [questionInfo, setQuestionInfo] = useState({});
  const [qpenInfoModal, setOpenInfoModal] = useState(false);

  const handleSectionChange = (panel) => (event, newExpanded) => {
    setTabValue(0);
    setSubSectionExpanded(false);
    setSectionExpanded(newExpanded ? panel : false);
  };
  const handleSubSectionChange = (panel) => (event, newExpanded) => {
    setTabValue(0);
    setSubSectionExpanded(newExpanded ? panel : false);
  };

  const handleTabChange = (event, value) => {
    setTabValue(value);
  };

  const getTabChange = (value) => {
    return tabValue + 1 > value ? value - 1 : tabValue;
  };

  const handleTextCommentChange = (
    event,
    sectionId,
    subSectionId,
    questionId
  ) => {
    const textComment = event.target.value.length > 1000 ? event.target.value.slice(0, 1000) : event.target.value;

    const result = data.map((section) => {
      if (section.sectionID === sectionId) {
        if (subSectionId) {
          // save  subSections
          section.subSections = section.subSections.map((subSection) => {
            if (subSection.subSectionID === subSectionId) {
              subSection.questions = subSection.questions.map((question) => {
                if (question.questionID === questionId) {
                  question.comment = textComment;
                }
                return question;
              });
            }
            return subSection;
          });
        } else {
          // save  questions
          section.questions = section.questions.map((question) => {
            if (question.questionID === questionId) {
              question.comment = textComment;
            }
            return question;
          });
        }
      }
      return section;
    });
    onComplete(Object.assign([], result));
  };

  const handleFileChange = (files, sectionId, subSectionId, questionId) => {
    const result = data.map((section) => {
      if (section.sectionID === sectionId) {
        if (subSectionId) {
          // save  subSections
          section.subSections = section.subSections.map((subSection) => {
            if (subSection.subSectionID === subSectionId) {
              subSection.questions = subSection.questions.map((question) => {
                if (question.questionID === questionId) {
                  question.files = files;
                }
                return question;
              });
            }
            return subSection;
          });
        } else {
          // save  questions
          section.questions = section.questions.map((question) => {
            if (question.questionID === questionId) {
              question.files = files;
            }
            return question;
          });
        }
      }
      return section;
    });
    onComplete(Object.assign([], result));
  };

  const handleChangeAnswers = (
    event,
    value,
    sectionId,
    subSectionId,
    questionId
  ) => {
    const result = data.map((section) => {
      if (section.sectionID === sectionId) {
        if (subSectionId) {
          // save  subSections
          section.subSections = section.subSections.map((subSection) => {
            if (subSection.subSectionID === subSectionId) {
              subSection.questions = subSection.questions.map((question) => {
                if (question.questionID === questionId) {
                  question.answers = question.answers.map((answer) => {
                    if (+answer.answerID === +value) {
                      answer.isChecked = true;
                      if (answer.choices.length === 0) { // ไม่เกี่ยวข้อง
                        question.isValid = true;
                        question.inProgress = false;
                      }
                      if (answer.choices.length !== 0) {
                        question.isValid = answer.choices.some((choice) => choice.isChecked);
                        question.inProgress = !answer.choices.some((choice) => choice.isChecked);
                      }
                      return answer;
                    }
                    answer.isChecked = false;
                    return answer;
                  });
                }
                return question;
              });
              subSection.isValid = !subSection.questions.some((question) => !question.isValid);
              subSection.inProgress = subSection.questions.some((question) => !question.isValid);
            }
            return subSection;
          });
          section.isValid = !section.subSections.some((subSection) => !subSection.isValid);
          section.inProgress = section.subSections.some((subSection) => !subSection.isValid);
        } else {
          // save  questions
          section.questions = section.questions.map((question) => {
            if (question.questionID === questionId) {
              question.answers = question.answers.map((answer) => {
                if (+answer.answerID === +value) {
                  answer.isChecked = true;
                  if (answer.choices.length === 0) { // ไม่เกี่ยวข้อง
                    question.isValid = true;
                    question.inProgress = false;
                  }
                  if (answer.choices.length !== 0) {
                    question.isValid = answer.choices.some((choice) => choice.isChecked);
                    question.inProgress = !answer.choices.some((choice) => choice.isChecked);
                  }
                  return answer;
                }
                answer.isChecked = false;
                return answer;
              });
            }
            return question;
          });
          section.isValid = !section.questions.some((question) => !question.isValid);
          section.inProgress = section.questions.some((question) => !question.isValid);
        }
      }
      return section;
    });
    onComplete(result);
  };

  const handleChangeChoice = (
    event,
    value,
    sectionId,
    subSectionId,
    questionId,
    answerId
  ) => {
    const result = data.map((section) => {
      if (section.sectionID === sectionId) {
        if (subSectionId) {
          // save  subSections
          section.subSections = section.subSections.map((subSection) => {
            if (subSection.subSectionID === subSectionId) {
              subSection.questions = subSection.questions.map((question) => {
                if (question.questionID === questionId) {
                  question.answers = question.answers.map((answer) => {
                    if (answer.answerID === answerId) {
                      answer.choices = answer.choices.map((choice) => {
                        if (+choice.choiceID === +value) {
                          choice.isChecked = true;
                          question.isValid = true;
                          question.inProgress = false;
                          return choice;
                        }
                        choice.isChecked = false;
                        return choice;
                      });
                    }
                    return answer;
                  });
                }
                return question;
              });
              subSection.isValid = !subSection.questions.some((question) => !question.isValid);
              subSection.inProgress = subSection.questions.some((question) => !question.isValid);
            }
            return subSection;
          });
          section.isValid = !section.subSections.some((subSection) => !subSection.isValid);
          section.inProgress = section.subSections.some((subSection) => !subSection.isValid);
        } else {
          // save  questions
          section.questions = section.questions.map((question) => {
            if (question.questionID === questionId) {
              question.answers = question.answers.map((answer) => {
                if (answer.answerID === answerId) {
                  answer.choices = answer.choices.map((choice) => {
                    if (+choice.choiceID === +value) {
                      choice.isChecked = true;
                      question.isValid = true;
                      question.inProgress = false;
                      return choice;
                    }
                    choice.isChecked = false;
                    return choice;
                  });
                }
                return answer;
              });
            }
            return question;
          });
          section.isValid = !section.questions.some((question) => !question.isValid);
          section.inProgress = section.questions.some((question) => !question.isValid);
        }
      }
      return section;
    });
    onComplete(result);
  };

  const getAnswerChecked = (answers) => {
    const result = answers.find((answer) => answer.isChecked);
    return result?.answerID || "";
  };
  const getChoiceChecked = (choices) => {
    const result = choices.find((choice) => choice.isChecked);
    return result?.choiceID || "";
  };

  const handleInfo = (question) => {
    setQuestionInfo(question);
    setOpenInfoModal(true);
  };

  const { t, i18n } = useTranslation();

  return (
    <>
      {data &&
        data.map((section) => {
          return (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={12} md={12} lg={12}>
                <Accordion
                  expanded={sectionExpanded === section.sectionID}
                  className={classes.accordion}
                  onChange={handleSectionChange(section.sectionID)}
                >
                  <AccordionSummary
                    expandIcon={
                      <ArrowForwardIosSharp sx={{ fontSize: "0.9rem" }} />
                    }
                    className={[
                      classes.accordionSummary,
                      section.isValid
                        ? classes.isValid
                        : section.inProgress
                          ? classes.inProgress
                          : "",
                    ].join(" ")}
                  >
                    <Typography className={classes.accordionTitle}>
                      <b>
                        {section.sectionCode} { i18n.language === "th" ? section.nameTH : section.nameEN  }
                      </b>
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails className={classes.accordionDetails}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={12} md={12} lg={12}>
                        <Toolbar className={classes.toolbar}>
                          <div className={classes.flexGrow} />
                          <Grid item xs={2} sm={2} md={2} lg={2}>
                            <span>{t("VendorSelf.fullScoreSection")}</span>
                          </Grid>
                          <Grid item xs={2} sm={2} md={2} lg={2}>
                            <span>{t("VendorSelf.scoreSection")}</span>
                          </Grid>
                        </Toolbar>
                      </Grid>
                      <Grid item xs={12} sm={12} md={12} lg={12}>
                        <Toolbar className={classes.toolbar}>
                          <div className={classes.flexGrow} />
                          <Grid item xs={2} sm={2} md={2} lg={2}>
                            <span>
                              {parseFloat(section.fullScore || 0).toFixed(2)}
                            </span>
                          </Grid>
                          <Grid item xs={2} sm={2} md={2} lg={2}>
                            <span>
                              {parseFloat(section.averageScore || 0).toFixed(
                                2
                              ) || "-"}
                            </span>
                          </Grid>
                        </Toolbar>
                      </Grid>
                    </Grid>
                    {section.subSections &&
                      section.subSections.map((subSection) => {
                        return (
                          <Grid container spacing={2}>
                            <Grid item xs={12} sm={12} md={12} lg={12}>
                              <Accordion
                                expanded={
                                  subSectionExpanded === subSection.subSectionID
                                }
                                className={classes.accordion}
                                onChange={handleSubSectionChange(
                                  subSection.subSectionID
                                )}
                              >
                                <AccordionSummary
                                  expandIcon={
                                    <ArrowForwardIosSharp
                                      sx={{ fontSize: "0.9rem" }}
                                    />
                                  }
                                  className={[
                                    classes.subAccordionSummary,
                                    subSection.isValid
                                      ? classes.isValid
                                      : subSection.inProgress
                                        ? classes.inProgress
                                        : "",
                                  ].join(" ")}
                                >
                                  <Typography
                                    className={classes.accordionTitle}
                                  >
                                    <b>{ i18n.language === "th" ? subSection.nameTH : subSection.nameEN  }</b>
                                  </Typography>
                                </AccordionSummary>
                                <AccordionDetails
                                  className={classes.accordionDetails}
                                >
                                  <Grid container spacing={2}>
                                    <Grid item xs={12} sm={12} md={12} lg={12}>
                                      <Toolbar className={classes.toolbar}>
                                        <div className={classes.flexGrow} />
                                        <Grid item xs={2} sm={2} md={2} lg={2}>
                                          <span>{t("VendorSelf.fullScoreSection")}</span>
                                        </Grid>
                                        <Grid item xs={2} sm={2} md={2} lg={2}>
                                          <span>{t("VendorSelf.scoreSection")}</span>
                                        </Grid>
                                      </Toolbar>
                                    </Grid>
                                    <Grid item xs={12} sm={12} md={12} lg={12}>
                                      <Toolbar className={classes.toolbar}>
                                        <div className={classes.flexGrow} />
                                        <Grid item xs={2} sm={2} md={2} lg={2}>
                                          <span>{subSection.fullScore}</span>
                                        </Grid>
                                        <Grid item xs={2} sm={2} md={2} lg={2}>
                                          <span>
                                            {parseFloat(
                                              subSection.averageScore || 0
                                            ).toFixed(2) || "-"}
                                          </span>
                                        </Grid>
                                      </Toolbar>
                                      {subSection.questions && (
                                        <Box sx={{ width: "100%" }}>
                                          <Box
                                            sx={{
                                              borderBottom: 1,
                                              borderColor: "divider",
                                            }}
                                          >
                                            <Tabs
                                              value={getTabChange(subSection.questions.length)}
                                              onChange={handleTabChange}
                                              variant="scrollable"
                                              scrollButtons
                                              allowScrollButtonsMobile
                                            >
                                              {subSection.questions.map(
                                                (question, index) => {
                                                  return (
                                                    <Tab
                                                      key={uuidv4()}
                                                      icon={
                                                        <Avatar
                                                          className={
                                                            question.isValid
                                                              ? classes.isValid
                                                              : question.inProgress
                                                                ? classes.inProgress
                                                                : ""
                                                          }
                                                        >
                                                          {
                                                            question.questionCode
                                                          }
                                                        </Avatar>
                                                      }
                                                    />
                                                  );
                                                }
                                              )}
                                            </Tabs>
                                          </Box>
                                          {subSection.questions.map(
                                            (question, index) => {
                                              return (
                                                <TabPanel
                                                  value={getTabChange(subSection.questions.length)}
                                                  index={index}
                                                >
                                                  <Grid container spacing={2}>
                                                    <Grid
                                                      item
                                                      xs={12}
                                                      sm={12}
                                                      md={10}
                                                      lg={10}
                                                    >
                                                      <Grid
                                                        container
                                                        spacing={2}
                                                      >
                                                        <Grid
                                                          item
                                                          xs={12}
                                                          sm={12}
                                                          md={12}
                                                          lg={12}
                                                        >
                                                          <p>
                                                            {question.questionCode}{". "}{i18n.language === "th" ? question.nameTH : question.nameEN}
                                                            {(question.flagRemark ||
                                                              question.flagExamRelDoc) && (
                                                                <InfoTwoTone
                                                                  onClick={() => {
                                                                    handleInfo(
                                                                      question
                                                                    );
                                                                  }}
                                                                  className={
                                                                    classes.iconInfo
                                                                  }
                                                                />
                                                              )}
                                                          </p>
                                                        </Grid>
                                                        <Grid
                                                          item
                                                          xs={12}
                                                          sm={12}
                                                          md={12}
                                                          lg={12}
                                                        >
                                                          <FormControl>
                                                            <RadioGroup
                                                              value={getAnswerChecked(
                                                                question.answers
                                                              )}
                                                              onChange={(
                                                                event,
                                                                value
                                                              ) => {
                                                                handleChangeAnswers(
                                                                  event,
                                                                  value,
                                                                  section.sectionID,
                                                                  subSection.subSectionID,
                                                                  question.questionID
                                                                );
                                                              }}
                                                            >
                                                              {question.answers.map(
                                                                (answer) => {
                                                                  return (
                                                                    <div>
                                                                      <FormControlLabel
                                                                        value={
                                                                          answer.answerID
                                                                        }
                                                                        control={
                                                                          <Radio
                                                                            disabled={
                                                                              isViewMode
                                                                            }
                                                                          />
                                                                        }
                                                                        label={ i18n.language === "th" ? answer.nameTH : answer.nameEN }
                                                                      />
                                                                      {answer.choices &&
                                                                        answer
                                                                          .choices
                                                                          .length >
                                                                        0 &&
                                                                        answer.isChecked && (
                                                                          <div
                                                                            style={{
                                                                              marginLeft: 50,
                                                                            }}
                                                                          >
                                                                            <b>
                                                                            {t("VendorSelf.answer")}
                                                                            </b>
                                                                            <RadioGroup
                                                                              value={getChoiceChecked(
                                                                                answer.choices
                                                                              )}
                                                                              onChange={(
                                                                                event,
                                                                                value
                                                                              ) => {
                                                                                handleChangeChoice(
                                                                                  event,
                                                                                  value,
                                                                                  section.sectionID,
                                                                                  subSection.subSectionID,
                                                                                  question.questionID,
                                                                                  answer.answerID
                                                                                );
                                                                              }}
                                                                            >
                                                                              {answer.choices.map(
                                                                                (
                                                                                  choice
                                                                                ) => {
                                                                                  return (
                                                                                    <FormControlLabel
                                                                                      value={
                                                                                        choice.choiceID
                                                                                      }
                                                                                      control={
                                                                                        <Radio
                                                                                          disabled={
                                                                                            isViewMode
                                                                                          }
                                                                                        />
                                                                                      }
                                                                                      label={ i18n.language === "th" ? choice.nameTH : choice.nameEN + " ( " + choice.choiceScoreShow + " คะแนน )" }
                                                                                    />
                                                                                  );
                                                                                }
                                                                              )}
                                                                            </RadioGroup>
                                                                          </div>
                                                                        )}
                                                                    </div>
                                                                  );
                                                                }
                                                              )}
                                                            </RadioGroup>
                                                          </FormControl>
                                                        </Grid>
                                                        <Grid
                                                          item
                                                          xs={12}
                                                          sm={12}
                                                          md={12}
                                                          lg={12}
                                                        >
                                                          <b>
                                                            {t("VendorSelf.vendorExplain")}
                                                          </b>
                                                          <TextField
                                                            multiline
                                                            label={t("VendorSelf.vendorExplainHint")}
                                                            disabled={
                                                              isViewMode
                                                            }
                                                            rows={3}
                                                            onChange={(
                                                              event
                                                            ) => {
                                                              handleTextCommentChange(
                                                                event,
                                                                section.sectionID,
                                                                subSection.subSectionID,
                                                                question.questionID
                                                              );
                                                            }}
                                                            value={
                                                              question.comment ||
                                                              ""
                                                            }
                                                            sx={{
                                                              width: "100%",
                                                            }}
                                                          />
                                                        </Grid>
                                                        <Grid
                                                          item
                                                          xs={12}
                                                          sm={12}
                                                          md={12}
                                                          lg={12}
                                                        >
                                                          <b>
                                                            {t("VendorSelf.otherFile")}
                                                          </b>
                                                          <FilesUploader
                                                            isViewMode={
                                                              isViewMode
                                                            }
                                                            data={
                                                              question.files
                                                            }
                                                            accept=".jpg, .jpeg, .png, .pdf, .xls, .xlsx, .doc, .docx"
                                                            maximumFileSize={
                                                              10485760
                                                            }
                                                            onComplete={(
                                                              files
                                                            ) => {
                                                              handleFileChange(
                                                                files,
                                                                section.sectionID,
                                                                subSection.subSectionID,
                                                                question.questionID
                                                              );
                                                            }}
                                                          />
                                                        </Grid>
                                                      </Grid>
                                                    </Grid>
                                                    {question.questionFileExam
                                                      .length > 0 && (
                                                        <Grid
                                                          item
                                                          xs={12}
                                                          sm={12}
                                                          md={2}
                                                          lg={2}
                                                          className={
                                                            classes.docLayout
                                                          }
                                                        >
                                                          <b>
                                                            ภาพตัวอย่าง /
                                                            เอกสารที่เกี่ยวข้อง
                                                          </b>
                                                          <br />
                                                          <br />
                                                          {question.questionFileExam.map(
                                                            (file) => {
                                                              return (
                                                                <div>
                                                                  <PopupImg
                                                                    width="40%"
                                                                    height="40%"
                                                                    alt="docimg"
                                                                    src={file.path}
                                                                    filename={file.name.split(".").shift()}
                                                                  />
                                                                </div>
                                                              );
                                                            }
                                                          )}
                                                        </Grid>
                                                      )}
                                                  </Grid>
                                                </TabPanel>
                                              );
                                            }
                                          )}
                                        </Box>
                                      )}
                                    </Grid>
                                  </Grid>
                                </AccordionDetails>
                              </Accordion>
                            </Grid>
                          </Grid>
                        );
                      })}
                    {section.questions && (
                      <Box sx={{ width: "100%" }}>
                        <Box
                          sx={{
                            borderBottom: 1,
                            borderColor: "divider",
                          }}
                        >
                          <Tabs
                            value={getTabChange(section.questions.length)}
                            onChange={handleTabChange}
                            variant="scrollable"
                            scrollButtons
                            allowScrollButtonsMobile
                          >
                            {section.questions.map((question, index) => {
                              return (
                                <Tab
                                  key={uuidv4()}
                                  icon={
                                    <Avatar
                                      className={
                                        question.isValid
                                          ? classes.isValid
                                          : question.inProgress
                                            ? classes.inProgress
                                            : ""
                                      }
                                    >
                                      {question.questionCode}
                                    </Avatar>
                                  }
                                />
                              );
                            })}
                          </Tabs>
                        </Box>
                        {section.questions.map((question, index) => {
                          return (
                            <TabPanel
                              value={getTabChange(section.questions.length)}
                              index={index}>
                              <Grid container spacing={2}>
                                <Grid item xs={12} sm={12} md={10} lg={10}>
                                  <Grid container spacing={2}>
                                    <Grid item xs={12} sm={12} md={12} lg={12}>
                                      <p>
                                        {question.questionCode}{". "}{i18n.language === "th" ? question.nameTH: question.nameEN}
                                        {(question.flagRemark ||
                                          question.flagExamRelDoc) && (
                                            <InfoTwoTone
                                              onClick={() => {
                                                handleInfo(question);
                                              }}
                                              className={classes.iconInfo}
                                            />
                                          )}
                                      </p>
                                    </Grid>
                                    <Grid item xs={12} sm={12} md={12} lg={12}>
                                      <FormControl>
                                        <RadioGroup
                                          value={getAnswerChecked(
                                            question.answers
                                          )}
                                          onChange={(event, value) => {
                                            handleChangeAnswers(
                                              event,
                                              value,
                                              section.sectionID,
                                              undefined,
                                              question.questionID
                                            );
                                          }}
                                        >
                                          {question.answers.map((answer) => {
                                            return (
                                              <div>
                                                <FormControlLabel
                                                  value={answer.answerID}
                                                  control={
                                                    <Radio
                                                      disabled={isViewMode}
                                                    />
                                                  }
                                                  label={i18n.language === "th" ? answer.nameTH || "" : answer.nameEN || "" }
                                                />
                                                {answer.choices &&
                                                  answer.choices.length > 0 &&
                                                  answer.isChecked && (
                                                    <div
                                                      style={{
                                                        marginLeft: 50,
                                                      }}
                                                    >
                                                      <b>{t("VendorSelf.answer")}</b>
                                                      <RadioGroup
                                                        value={getChoiceChecked(
                                                          answer.choices
                                                        )}
                                                        onChange={(
                                                          event,
                                                          value
                                                        ) => {
                                                          handleChangeChoice(
                                                            event,
                                                            value,
                                                            section.sectionID,
                                                            undefined,
                                                            question.questionID,
                                                            answer.answerID
                                                          );
                                                        }}
                                                      >
                                                        {answer.choices.map(
                                                          (choice) => {
                                                            return (
                                                              <FormControlLabel
                                                                value={
                                                                  choice.choiceID
                                                                }
                                                                control={
                                                                  <Radio
                                                                    disabled={
                                                                      isViewMode
                                                                    }
                                                                  />
                                                                }
                                                                label={
                                                                  (i18n.language === "th" ? choice.nameTH || "" : choice.nameEN || "") +
                                                                  " ( " +
                                                                  (choice.choiceScoreShow || "") +
                                                                  " คะแนน )"
                                                                }
                                                              />
                                                            );
                                                          }
                                                        )}
                                                      </RadioGroup>
                                                    </div>
                                                  )}
                                              </div>
                                            );
                                          })}
                                        </RadioGroup>
                                      </FormControl>
                                    </Grid>
                                    <Grid item xs={12} sm={12} md={12} lg={12}>
                                      <b>{t("VendorSelf.vendorExplain")}</b>
                                      <TextField
                                        multiline
                                        label={t("VendorSelf.vendorExplainHint")}
                                        disabled={isViewMode}
                                        rows={3}
                                        value={question.comment || ""}
                                        onChange={(event) => {
                                          handleTextCommentChange(
                                            event,
                                            section.sectionID,
                                            undefined,
                                            question.questionID
                                          );
                                        }}
                                        sx={{ width: "100%" }}
                                      />
                                    </Grid>
                                    <Grid item xs={12} sm={12} md={12} lg={12}>
                                      <b>{t("VendorSelf.otherFile")}</b>
                                      <FilesUploader
                                        data={question.files}
                                        isViewMode={isViewMode}
                                        accept=".jpg, .jpeg, .png, .pdf, .xls, .xlsx, .doc, .docx"
                                        maximumFileSize={10485760}
                                        onComplete={(files) => {
                                          handleFileChange(
                                            files,
                                            section.sectionID,
                                            undefined,
                                            question.questionID
                                          );
                                        }}
                                      />
                                    </Grid>
                                  </Grid>
                                </Grid>
                                {question.questionFileExam.length > 0 && (
                                  <Grid
                                    item
                                    xs={12}
                                    sm={12}
                                    md={2}
                                    lg={2}
                                    className={classes.docLayout}
                                  >
                                    <b>{t("VendorSelf.relateFile")}</b>
                                    <br />
                                    <br />
                                    {question.questionFileExam.map((file) => {
                                      return (
                                        <div>
                                          <PopupImg
                                            width="40%"
                                            height="40%"
                                            alt="docimg"
                                            src={file.path}
                                            filename={file.name.split(".").shift()}
                                          />
                                        </div>
                                      );
                                    })}
                                  </Grid>
                                )}
                              </Grid>
                            </TabPanel>
                          );
                        })}
                      </Box>
                    )}
                  </AccordionDetails>
                </Accordion>
              </Grid>
            </Grid>
          );
        })}
      <Dialog open={qpenInfoModal} onClose={() => setOpenInfoModal(false)}>
        <>
          {questionInfo.flagRemark && (
            <>
              <DialogTitle style={{ color: "red" }} id="alert-dialog-title">
                {"หมายเหตุ"}
              </DialogTitle>
              <DialogContent style={{ color: "#263238", marginLeft: "15px" }}>
                <DialogContentText id="alert-dialog-description">
                  <div
                    dangerouslySetInnerHTML={{
                      __html: questionInfo.remarkTh,
                    }}
                  ></div>
                </DialogContentText>
              </DialogContent>
            </>
          )}
          <Divider variant="middle" />
          {questionInfo.flagExamRelDoc && (
            <>
              <DialogTitle id="alert-dialog-title">
                {"คำอธิบายตัวอย่างเอกสารที่เกี่ยวข้อง"}
              </DialogTitle>
              <DialogContent style={{ color: "#263238", marginLeft: "15px" }}>
                <DialogContentText id="alert-dialog-description">
                  <div
                    dangerouslySetInnerHTML={{
                      __html: questionInfo.examRelDocTh,
                    }}
                  ></div>
                </DialogContentText>
              </DialogContent>
            </>
          )}
        </>
      </Dialog>
    </>
  );
}

QuestionSet.propTypes = {
  className: PropTypes.string,
  data: PropTypes.array,
  isViewMode: PropTypes.bool,
};

export default QuestionSet;
